/* eslint-disable max-len */
/* stylelint-disable max-line-length */
import {memo} from 'react';

import styled from 'styled-components';

import {getLocalizationService} from '~/shared/services/localisationService';
import ImageWithAlt from '~/shared/components/ImageWithAlt';
import {flipOnLTR, media, rotateOnLTR} from '~/shared/theme/utils';
import scooberTag from '~/assets/images/icons/scoober_tag.svg';
import restaurantTag from '~/assets/images/icons/restaurant_tag.svg';
import {body14Bold} from '~/shared/theme/typography';
import {flexCenter} from '~/shared/theme/FlexLayout';
import {useIsMinLargeMobile, useIsMinLargeTablet} from '~/shared/hooks/deviceInfo';

const ScooberLogoIcon = styled(ImageWithAlt).attrs({noAlt: true})``;

const ScooberWrapper = styled.div.attrs({className: 'transparent-bg-on-contrast'})`
  position: absolute;
  display: flex;
  border-radius: 100px;
  align-items: center;
  gap: 4px;
  padding: 2px;
  background: ${({theme}) => theme.colors.primaryText};
  box-shadow: ${({theme}) => theme.shadows.shadow9};
  z-index: 80;
  ${flipOnLTR`
    padding-left: 6px;
  `}
`;

const ScooberMenuWrapper = styled(ScooberWrapper)`
  bottom: 16px;
  ${flipOnLTR`
    left: 16px;
    ${media.minLargeMobile`
      bottom: 0;
      left: 0;
    `}
  `}
`;

const CenteredWrapper = styled.div`
  width: 570px;
  margin: auto auto 16px;
`;

const ScooberRestaurantCardWrapper = styled(ScooberWrapper)`
  bottom: 12px;
  ${flipOnLTR`
    left: 12px;
  `}
`;

const ScooberText = styled.div`
  ${body14Bold}
  color: ${({theme}) => theme.colors.charcoal80};
`;

const ScooberTextRestaurant = styled(ScooberText)`
  line-height: 1;
`;

const IconWrapper = styled.div<{darkBg?: boolean}>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${({theme, darkBg}) => (darkBg ? theme.colors.backgroundInteractive : theme.colors.contentBrand)};
  ${flexCenter}
  ${rotateOnLTR}
`;

const DiagonalBadge = memo(({isScoober, isStore}: {isScoober?: boolean; isStore?: boolean}) => {
  const {t} = getLocalizationService();
  return (
    <ScooberMenuWrapper>
      <IconWrapper darkBg={!isScoober}>
        <ScooberLogoIcon src={isScoober ? scooberTag : restaurantTag} />
      </IconWrapper>
      <ScooberText>{t(isScoober ? 'scoober_filter_label' : isStore ? 'own_delivery_store' : 'own_delivery')}</ScooberText>
    </ScooberMenuWrapper>
  );
});

const RestaurantCardBadge = memo(({isScoober, isStore}: {isScoober?: boolean; isStore?: boolean}) => {
  const {t} = getLocalizationService();
  return (
    <ScooberRestaurantCardWrapper>
      <IconWrapper darkBg={!isScoober}>
        <ScooberLogoIcon src={isScoober ? scooberTag : restaurantTag} />
      </IconWrapper>
      <ScooberTextRestaurant>{t(isScoober ? 'scoober_filter_label' : isStore ? 'own_delivery_store' : 'own_delivery')}</ScooberTextRestaurant>
    </ScooberRestaurantCardWrapper>
  );
});

const DeliveryBadge = ({isScoober, top, isStore}: {isScoober?: boolean; top?: boolean; isStore?: boolean}) => {
  const isMinLargeMobile = useIsMinLargeMobile();
  const isMinLargeTablet = useIsMinLargeTablet();

  const centerLogo = isMinLargeMobile && !isMinLargeTablet;
  if (!top) {
    return <RestaurantCardBadge isStore={isStore} isScoober={isScoober} />;
  }
  if (centerLogo) {
    return (
      <CenteredWrapper>
        <DiagonalBadge isStore={isStore} isScoober={isScoober} />
      </CenteredWrapper>
    );
  }
  return <DiagonalBadge isStore={isStore} isScoober={isScoober} />;
};

export default memo(DeliveryBadge);
